import React, { forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

/**
 * Button Component
 *
 * A flexible, accessible button component that supports multiple variants,
 * sizes, states, and custom styling through the theme system.
 *
 * @example
 * // Primary button with icon
 * <Button
 *   variant="primary"
 *   size="medium"
 *   icon={<IconComponent />}
 *   onClick={handleClick}
 * >
 *   Click me
 * </Button>
 *
 * // Secondary button with custom colors
 * <Button
 *   variant="secondary"
 *   defaultColor="secondary"
 *   hoverColor="secondaryDark"
 *   loading={isLoading}
 * >
 *   Submit
 * </Button>
 */

// Size configurations for consistent scaling
const BUTTON_SIZES = {
  small: {
    fontSize: '1.4rem',
    padding: '0.7rem 1.8rem',
    borderRadius: '9999px',
    iconGap: '0.3em',
  },
  medium: {
    fontSize: '1.6rem',
    padding: '0.9rem 2.2rem',
    borderRadius: '14px',
    iconGap: '0.4em',
  },
  large: {
    fontSize: '1.8rem',
    padding: '1.1rem 2.6rem',
    borderRadius: '14px',
    iconGap: '0.5em',
  },
};

// Variant style generators for clean separation of concerns
const getVariantStyles = (
  variant,
  defaultColor,
  hoverColor,
  theme,
  disabled,
  selected,
) => {
  const baseStyles = {
    transition: theme.transitions.default,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    border: 'none',
    outline: 'none',
  };

  switch (variant) {
    case 'primary': {
      const bg = disabled
        ? theme.colors.greyLight
        : theme.colors[defaultColor] || theme.colors.primary;
      const bgDark = theme.colors[hoverColor] || theme.colors.primaryDark;
      return {
        ...baseStyles,
        backgroundColor: bg,
        color: disabled ? theme.colors.grey : theme.colors.white,
        border: 'none',
        boxShadow: disabled ? 'none' : `0 4px 14px ${bg}40`,

        '&:hover:not(:disabled)': {
          backgroundColor: bgDark,
          transform: 'translateY(-1px)',
          boxShadow: `0 6px 20px ${bg}50`,
        },

        '&:active:not(:disabled)': {
          transform: 'translateY(0)',
          boxShadow: `0 2px 8px ${bg}30`,
        },
      };
    }

    case 'secondary': {
      const color = theme.colors[defaultColor] || theme.colors.primary;
      const colorDark = theme.colors[hoverColor] || theme.colors.primaryDark;
      return {
        ...baseStyles,
        backgroundColor: disabled ? 'transparent' : `${color}14`,
        color: disabled ? theme.colors.grey : color,
        border: `1.5px solid ${disabled ? theme.colors.greyLight : color}`,

        '&:hover:not(:disabled)': {
          backgroundColor: color,
          color: theme.colors.white,
          borderColor: colorDark,
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 14px ${color}40`,
        },

        '&:active:not(:disabled)': {
          transform: 'translateY(0)',
          boxShadow: `0 2px 8px ${color}20`,
        },
      };
    }

    case 'ghost':
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: disabled
          ? theme.colors.grey
          : theme.colors[defaultColor] || theme.colors.primary,
        border: 'none',

        '&:hover:not(:disabled)': {
          backgroundColor: `${theme.colors[defaultColor] || theme.colors.primary}10`,
          color: theme.colors[hoverColor] || theme.colors.primaryDark,
        },

        '&:active:not(:disabled)': {
          backgroundColor: `${theme.colors[defaultColor] || theme.colors.primary}20`,
        },
      };

    // A selectable pill: tip amounts, pickup days, periods and time slots
    // (#243). Unselected is outlined white; selected fills with the colour.
    case 'chip': {
      const color = theme.colors[defaultColor] || theme.colors.primary;
      return {
        ...baseStyles,
        backgroundColor: selected ? color : theme.colors.white,
        color: selected ? theme.colors.white : theme.colors.greyDark,
        border: `1.5px solid ${selected ? color : theme.colors.greyLight}`,
        boxShadow: selected
          ? `0 6px 20px ${color}30, inset 0 1px 0 ${theme.colors.white}1F`
          : 'none',
        transform: 'none',

        '&:hover:not(:disabled)': {
          borderColor: color,
          backgroundColor: selected ? color : `${color}10`,
        },
      };
    }

    case 'danger':
      return {
        ...baseStyles,
        backgroundColor: disabled ? theme.colors.greyLight : theme.colors.error,
        color: disabled ? theme.colors.grey : theme.colors.white,
        border: 'none',

        '&:hover:not(:disabled)': {
          backgroundColor: '#c82333', // Darker red
          transform: 'translateY(-1px)',
          boxShadow: theme.shadows.md,
        },

        '&:active:not(:disabled)': {
          transform: 'translateY(0)',
          boxShadow: theme.shadows.sm,
        },
      };

    default:
      return baseStyles;
  }
};

const StyledButton = styled.button`
  ${({ theme }) => theme.mixins.textP2};
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: center;
  text-decoration: none;
  vertical-align: middle;
  user-select: none;
  position: relative;
  overflow: hidden;

  font-size: ${({ $size }) => BUTTON_SIZES[$size].fontSize};
  padding: ${({ $size }) => BUTTON_SIZES[$size].padding};
  border-radius: ${({ $size }) => BUTTON_SIZES[$size].borderRadius};

  ${({ $variant, $defaultColor, $hoverColor, theme, disabled, $selected }) =>
    getVariantStyles(
      $variant,
      $defaultColor,
      $hoverColor,
      theme,
      disabled,
      $selected,
    )};

  &:focus-visible {
    box-shadow: 0 0 0 3px
      ${({ theme, $variant }) =>
        $variant === 'danger'
          ? `${theme.colors.error}40`
          : `${theme.colors.primary}40`};
    outline: none;
  }

  ${({ $loading }) =>
    $loading &&
    `
    pointer-events: none;
    position: relative;
  `}

  ${({ $fullWidth }) =>
    $fullWidth &&
    `
    width: 100%;
    display: block;
  `}
  
  svg {
    transition: transform 0.2s ease-in-out;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) svg {
    transform: ${({ $animateIcon }) => ($animateIcon ? 'scale(1.1)' : 'none')};
  }
`;

const ButtonContent = styled.span`
  ${({ theme }) => theme.mixins.flexRowCenter};
  gap: ${({ $size }) => BUTTON_SIZES[$size].iconGap};
  position: relative;
  z-index: 1;

  ${({ $loading }) => $loading && 'opacity: 0;'}
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 1.2em;
  height: 1.2em;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const Button = forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'medium',
      defaultColor = 'primary',
      hoverColor = 'primaryDark',
      onClick,
      onFocus,
      onBlur,
      type = 'button',
      disabled = false,
      loading = false,
      selected = false,
      icon = null,
      iconPosition = 'left',
      animateIcon = true,
      fullWidth = false,
      className = '',
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'data-testid': testId,
      ...rest
    },
    ref,
  ) => {
    // Memoized click handler to prevent unnecessary re-renders
    const handleClick = useCallback(
      (event) => {
        if (disabled || loading) {
          event.preventDefault();
          return;
        }

        if (onClick) {
          try {
            onClick(event);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Button onClick error:', error);
            // Error boundary will catch this if it propagates
            throw error;
          }
        }
      },
      [onClick, disabled, loading],
    );

    const handleFocus = useCallback(
      (event) => {
        if (onFocus && !disabled) {
          onFocus(event);
        }
      },
      [onFocus, disabled],
    );

    const handleBlur = useCallback(
      (event) => {
        if (onBlur && !disabled) {
          onBlur(event);
        }
      },
      [onBlur, disabled],
    );

    // Validate color props exist in theme
    const validatedDefaultColor = defaultColor;
    const validatedHoverColor = hoverColor;

    // Determine if button should be disabled
    const isDisabled = disabled || loading;

    // Generate accessible label
    const accessibleLabel =
      ariaLabel || (typeof children === 'string' ? children : undefined);

    // Loading state should announce to screen readers
    const ariaLive = loading ? 'polite' : undefined;
    const ariaDescribedByWithLoading = loading
      ? `${ariaDescribedBy || ''} button-loading`.trim()
      : ariaDescribedBy;

    return (
      <StyledButton
        ref={ref}
        type={type}
        $variant={variant}
        $size={size}
        $defaultColor={validatedDefaultColor}
        $hoverColor={validatedHoverColor}
        disabled={isDisabled}
        $selected={selected}
        $loading={loading}
        $animateIcon={animateIcon}
        $fullWidth={fullWidth}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={className}
        aria-label={accessibleLabel}
        aria-describedby={ariaDescribedByWithLoading}
        aria-disabled={isDisabled}
        aria-live={ariaLive}
        data-testid={testId || 'button'}
        {...rest}
      >
        {/* Screen reader loading announcement */}
        {loading && (
          <span id="button-loading" className="sr-only">
            Loading
          </span>
        )}

        <ButtonContent $size={size} $loading={loading}>
          {icon && iconPosition === 'left' && (
            <IconWrapper aria-hidden="true">{icon}</IconWrapper>
          )}

          {children}

          {icon && iconPosition === 'right' && (
            <IconWrapper aria-hidden="true">{icon}</IconWrapper>
          )}
        </ButtonContent>

        {loading && <LoadingSpinner aria-hidden="true" />}
      </StyledButton>
    );
  },
);

Button.displayName = 'Button';

Button.propTypes = {
  /** Button content - can be text, elements, or components */
  children: PropTypes.node.isRequired,

  /** Visual style variant */
  variant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'danger', 'chip']),

  /** Button size affecting padding and font size */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /** Theme color key for default state */
  defaultColor: PropTypes.string,

  /** Theme color key for hover state */
  hoverColor: PropTypes.string,

  /** Click event handler */
  onClick: PropTypes.func,

  /** Focus event handler */
  onFocus: PropTypes.func,

  /** Blur event handler */
  onBlur: PropTypes.func,

  /** HTML button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),

  /** Whether button is disabled */
  disabled: PropTypes.bool,

  /** Whether button is in loading state */
  loading: PropTypes.bool,

  /** `chip` variant only: whether this option is the chosen one */
  selected: PropTypes.bool,

  /** Icon element to display */
  icon: PropTypes.node,

  /** Position of icon relative to text */
  iconPosition: PropTypes.oneOf(['left', 'right']),

  /** Whether to animate icon on hover */
  animateIcon: PropTypes.bool,

  /** Whether button should take full width of container */
  fullWidth: PropTypes.bool,

  /** Additional CSS classes */
  className: PropTypes.string,

  /** Accessible label for screen readers */
  'aria-label': PropTypes.string,

  /** ID of element that describes the button */
  'aria-describedby': PropTypes.string,

  /** Test identifier for automated testing */
  'data-testid': PropTypes.string,
};

export default Button;
